import { useEffect } from 'react';
import { BacksolveSourceRow } from 'api';
import { useStore } from 'common/store';
import { CurrentCompanyMeasurementDate } from 'common/types/measurementDate';
import { UseStoreValues } from 'common/types/store';
import { buildMutualFundMarksSourceRows } from 'pages/Portfolio/pages/CompanySummary/components/CompanyOverview/MutualFundMarksCard/MutualFundMarksCard.utils';
import { useGetCompanyMutualFundMarks, useGetCompanyZanbatoMarksValuation } from 'services/hooks/companies';

type UseMutualFundMarksSourceRowParams = {
  backsolveType: string | undefined;
  measurementDate: CurrentCompanyMeasurementDate | null | undefined;
  getSourceRows: () => BacksolveSourceRow[];
  resetSourceRowsSheetWithRows: (rows: BacksolveSourceRow[]) => void;
};

const useMutualFundMarksSourceRows = ({
  backsolveType,
  measurementDate,
  getSourceRows,
  resetSourceRowsSheetWithRows,
}: UseMutualFundMarksSourceRowParams): void => {
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const companyId: number | undefined = storeValue?.companyInfo?.id;
  const isMfmApproach = backsolveType === 'mutual_fund_marks';

  const { data: valuationData } = useGetCompanyZanbatoMarksValuation(isMfmApproach ? companyId : undefined);
  const { data: edgarData } = useGetCompanyMutualFundMarks(isMfmApproach ? companyId : undefined);

  useEffect(() => {
    if (!isMfmApproach) {
      return;
    }
    // Row window is relative to the measurement date.
    const measurementDateStr = measurementDate?.date;
    if (!measurementDateStr) {
      return;
    }

    // Same builder the preview modal uses, so rows/ids/titles match exactly.
    const desiredRows = buildMutualFundMarksSourceRows(
      valuationData?.valuations ?? [],
      edgarData?.marks ?? [],
      measurementDateStr
    );
    if (desiredRows.length === 0) {
      return;
    }

    // Diff by id so Zanbato or EDGAR adds its own rows instead of overwriting the ones already added.
    const currentRows = getSourceRows();
    const existingIds = new Set(currentRows.map(row => row.source_record_id).filter(Boolean));
    const missingRows = desiredRows.filter(row => !existingIds.has(row.source_record_id));
    if (missingRows.length === 0) {
      return;
    }

    resetSourceRowsSheetWithRows([...currentRows, ...missingRows]);
    // ESLint exception to avoid adding getSourceRows and resetSourceRowsSheetWithRows in the dep array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMfmApproach, valuationData, edgarData, measurementDate?.date]);
};

export default useMutualFundMarksSourceRows;
